import { type DateRange, dateAdd, getRange, toDateString } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeeRoute } from "../../app-routes";
import { availabilitiesQuery } from "../all/calendar.queries";
import { documentTagsQuery, employeeDocumentsQuery } from "../all/records.queries";
import { employeeAbsencesQuery } from "./calendar.queries";
import type { EmployeeLayoutRoute } from "./employee.route";

const STALE_TIME = 30_000;

/** Same shape the old pages produced for their `routeTitle`. */
function titled(label: string) {
    return ({ match, context }: { match: { params: unknown }; context: any }): string =>
        `${label}: ${context.app.getEmployee((match.params as { employeeId: number }).employeeId)?.name ?? ""}`;
}

function defaultRange(): DateRange {
    const from = getRange(new Date(), "month").from;
    return { from, to: dateAdd(from, { months: 3 }) };
}

export function createEmployeeAbsencesRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "absences",
        staticData: { title: titled("Abwesenheiten"), menu: employeeRoute("absences") },
        validateSearch: (search: Record<string, unknown>): { display_absence?: number } => ({
            display_absence: search.display_absence ? Number(search.display_absence) : undefined,
        }),
        loader: ({ params, context: { app, queryClient } }) =>
            queryClient.fetchQuery({ ...employeeAbsencesQuery(app, params.employeeId), staleTime: STALE_TIME }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-absences-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .absences=${match.loaderData ?? []}
                .displayAbsence=${match.search.display_absence}
            ></ptc-employees-absences-single>`,
    }).lazy(lazyElement(() => import("./employees-absences-single")));
}

export function createEmployeeAvailabilitiesRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "availabilities",
        staticData: { title: titled("Verfügbarkeiten"), menu: employeeRoute("availabilities") },
        validateSearch: (search: Record<string, unknown>): Partial<DateRange> & { date?: DateString } => ({
            date: search.date as DateString | undefined,
            from: search.from as DateString | undefined,
            to: search.to as DateString | undefined,
        }),
        beforeLoad: ({ search, params }): void => {
            if (!search.from || !search.to || !search.date) {
                redirectTo(`/employees/${params.employeeId}/availabilities`, {
                    ...search,
                    ...(search.from && search.to ? {} : defaultRange()),
                    date: search.date ?? toDateString(new Date()),
                });
            }
        },
        loaderDeps: ({ search }) => ({ from: search.from!, to: search.to! }),
        loader: ({ deps, params, context: { app, queryClient } }) =>
            queryClient.fetchQuery({
                ...availabilitiesQuery(app, deps, [params.employeeId]),
                staleTime: STALE_TIME,
            }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-availabilities-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .range=${{ from: match.search.from, to: match.search.to }}
                .availabilities=${match.loaderData ?? []}
            ></ptc-employees-availabilities-single>`,
    }).lazy(lazyElement(() => import("./employees-availabilities-single")));
}

export function createEmployeeDocumentsRoute(parent: EmployeeLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "documents",
        staticData: { title: titled("Dokumente"), menu: employeeRoute("documents") },
        validateSearch: (search: Record<string, unknown>): { openDoc?: number } => ({
            openDoc: search.openDoc ? Number(search.openDoc) : undefined,
        }),
        loader: async ({ params, context: { app, queryClient } }) => {
            const [documents, documentTags] = await Promise.allSettled([
                queryClient.fetchQuery({ ...employeeDocumentsQuery(app, params.employeeId), staleTime: STALE_TIME }),
                queryClient.fetchQuery({ ...documentTagsQuery(app), staleTime: STALE_TIME }),
            ]);
            return {
                documents: documents.status === "fulfilled" ? documents.value : [],
                documentTags: documentTags.status === "fulfilled" ? documentTags.value : [],
            };
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-documents-single
                class="fullbleed"
                .employeeId=${match.params.employeeId}
                .documents=${match.loaderData?.documents ?? []}
                .documentTags=${match.loaderData?.documentTags ?? []}
                .openDoc=${match.search.openDoc}
            ></ptc-employees-documents-single>`,
    }).lazy(lazyElement(() => import("./employees-documents-single")));
}

export function createEmployeeCalendarRoutes(parent: EmployeeLayoutRoute) {
    return [
        createEmployeeAbsencesRoute(parent),
        createEmployeeAvailabilitiesRoute(parent),
        createEmployeeDocumentsRoute(parent),
    ] as const;
}
